import { createClient } from '@supabase/supabase-js'
import { isCacheablePath, writeCache, readCache } from './offlineCache'
import { isQueueablePath, enqueueWrite, flushOutbox } from './offlineOutbox'

const SUPABASE_URL     = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

// In dev: Vite proxies /api → localhost:3001 (no env var needed)
// In prod: VITE_API_URL = https://your-backend.up.railway.app
const BASE = (import.meta.env.VITE_API_URL || '') + '/api'

async function getAuth() {
  const { data: { session } } = await supabase.auth.getSession()
  return { token: session?.access_token || null, userId: session?.user?.id || null }
}

function withOfflineFlag(cached) {
  const v = cached.value
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return { ...v, _offline: true, _cachedAt: cached.cachedAt }
  }
  return v
}

// SOVRANOVA's spoken (ElevenLabs) voice is a per-user choice. Read the chosen
// voice id from the same localStorage the settings picker writes; default Sarah.
function readVoicePref() {
  try {
    const v = JSON.parse(localStorage.getItem('sovranova_settings_v1') || '{}').voiceId
    return v || 'EXAVITQu4vr4xnSDxMaL'
  } catch { return 'EXAVITQu4vr4xnSDxMaL' }
}

// The user's self-declared region/jurisdiction (set via Settings > Region),
// used by the backend to apply the right AI-disclosure / affect-sensing rules
// (see backend/services/compliance/jurisdictionPolicy.js). Never IP-geolocated
// — plain localStorage key, not part of the settings_v1 blob, absent by default.
function readRegionPref() {
  try {
    return localStorage.getItem('sovranova_region') || ''
  } catch { return '' }
}

async function request(method, path, body = null) {
  const { token, userId } = await getAuth()
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const region = readRegionPref()
  if (region) headers['x-sovranova-region'] = region
  // The user's own local hour. The server would otherwise use ITS clock to
  // decide morning/afternoon/evening, so an overseas user gets "Good morning"
  // at 11pm — which breaks the one thing this product is built on: that it
  // knows you. Sent as a plain 0-23 integer; no location is disclosed.
  try {
    headers['x-sovranova-hour'] = String(new Date().getHours())
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz) headers['x-sovranova-tz'] = tz
  } catch (_) {}

  const cacheable = method === 'GET' && isCacheablePath(path)

  // Inject the user's chosen spoken-guidance voice on /voice/speak unless the
  // caller set one explicitly (e.g. the Settings preview button).
  if (method === 'POST' && path === '/voice/speak') {
    body = { ...(body || {}) }
    if (!body.voiceId) body.voiceId = readVoicePref()
  }

  let res
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    })
  } catch (netErr) {
    // Network unreachable (offline, or connection refused mid-deploy).
    // Fall back to the user's cached memory so SOVRANOVA still knows them.
    if (cacheable) {
      const cached = await readCache(userId, path)
      if (cached) return withOfflineFlag(cached)
    }
    // A durable write while offline — queue it; replay on reconnect. Nothing lost.
    if (method === 'POST' && isQueueablePath(path)) {
      const queued = await enqueueWrite(userId, path, body)
      if (queued) return { _offline: true, _queued: true }
    }
    throw netErr
  }

  if (!res.ok) {
    // Backend reachable but failing (e.g. 503 during an upgrade) — serve memory if we have it.
    if (cacheable && res.status >= 500) {
      const cached = await readCache(userId, path)
      if (cached) return withOfflineFlag(cached)
    }
    // Durable write hitting a 5xx (server mid-upgrade) — queue and replay later.
    if (method === 'POST' && res.status >= 500 && isQueueablePath(path)) {
      const queued = await enqueueWrite(userId, path, body)
      if (queued) return { _offline: true, _queued: true }
    }
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }

  const json = await res.json()
  if (cacheable) writeCache(userId, path, json)
  return json
}

// NDJSON streaming POST — invokes onLine(parsedObject) for each line as the
// server writes it. Resolves when the stream closes; throws on a non-2xx
// response (so callers can fall back to the non-streaming endpoint).
async function streamRequest(path, body, onLine) {
  const { token } = await getAuth()
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  try {
    headers['x-sovranova-hour'] = String(new Date().getHours())
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz) headers['x-sovranova-tz'] = tz
  } catch (_) {}
  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: JSON.stringify(body || {}) })
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)
  const reader = res.body.getReader()
  const dec = new TextDecoder()
  let buf = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buf += dec.decode(value, { stream: true })
    let i
    while ((i = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, i).trim(); buf = buf.slice(i + 1)
      if (!line) continue
      try { onLine(JSON.parse(line)) } catch (_) {}
    }
  }
}

export const api = {
  get:  (path)       => request('GET',  path),
  post: (path, body) => request('POST', path, body),
  put:  (path, body) => request('PUT',  path, body),
  del:  (path)        => request('DELETE', path),
  stream: streamRequest,
}

// ── Offline write replay ──────────────────────────────────────────────────
// Raw sender used only for replaying queued writes. Uses fetch directly (not
// request()) so a still-failing replay rejects cleanly without re-queueing
// into a loop. Resolves on 2xx, rejects otherwise.
async function rawSend(path, body) {
  const { token } = await getAuth()
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST', headers, body: body ? JSON.stringify(body) : null,
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json().catch(() => ({}))
}

// Replay anything queued offline. Safe to call repeatedly; no-op when empty.
export async function syncOfflineWrites() {
  try {
    const { userId } = await getAuth()
    if (!userId) return { sent: 0, remaining: 0 }
    return await flushOutbox(userId, rawSend)
  } catch (_) {
    return { sent: 0, remaining: -1 }
  }
}

// Auto-replay the moment the browser regains connectivity.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { syncOfflineWrites() })
}

